import Announcement from "../components/announcement";
import TrainingHeader from "../components/trainingHeader";
import cac from "../assets/images/in Calvary Arrows.svg";
import twentyTwenty from "../assets/images/bootcamp2020.svg";
import twentyTw0 from "../assets/images/bootcamp2022.svg";
import { useEffect, useState, useCallback } from "react";
import { getPrograms } from "../lib/firestoreContent";

const fallbackProgramImages = {
  "Online Robotics Bootcamp": "/images/bootcamp2022.svg",
  "Web Development Fundamentals": "/images/Custom Coding.svg",
  "AI & Machine Learning Intro": "/images/bootcamp2020.svg",
  "Hardware Programming & Embedded Systems": "/images/ConnectionHardware.svg",
  "UI/UX Design for Tech": "/images/graphic design.svg",
  "Training Session at Calvary Arrows": cac,
  "Bootcamp 2020": twentyTwenty,
  "Bootcamp 2022": twentyTw0,
};

const fallbackPrograms = [
  {
    id: "prog-1",
    title: "Online Robotics Bootcamp",
    description: "A 1-year online program teaching robotics fundamentals, Arduino programming, and IoT projects.\n\nStudents will gain hands-on experience building working prototypes and interactive hardware circuits.",
    status: "Open",
    programType: "upcoming",
    imageUrl: "/images/bootcamp2022.svg",
  },
  {
    id: "prog-2",
    title: "Web Development Fundamentals",
    description: "Learn HTML, CSS, JavaScript, and React to build modern, interactive web applications.\n\nFrom basic semantics to state-driven user interfaces and deployment.",
    status: "Open",
    programType: "upcoming",
    imageUrl: "/images/Custom Coding.svg",
  },
  {
    id: "prog-3",
    title: "AI & Machine Learning Intro",
    description: "Introduction to artificial intelligence concepts, Python programming, and practical ML models.\n\nExplore how predictive models and intelligent algorithms are shaping the modern world.",
    status: "Open",
    programType: "upcoming",
    imageUrl: "/images/bootcamp2020.svg",
  },
  {
    id: "prog-4",
    title: "Hardware Programming & Embedded Systems",
    description: "Hands-on training in microcontrollers, sensors, and circuit design to innovate with technology.\n\nMaster Arduino, sensor integration, and motor control.",
    status: "Open",
    programType: "upcoming",
    imageUrl: "/images/ConnectionHardware.svg",
  },
  {
    id: "prog-5",
    title: "UI/UX Design for Tech",
    description: "User interface and user experience design principles for building modern hardware and software systems.\n\nCovers user research, wireframing, Figma prototyping, and design systems.",
    status: "Open",
    programType: "upcoming",
    imageUrl: "/images/graphic design.svg",
  },
  {
    id: "hist-1",
    title: "Training Session at Calvary Arrows",
    description: "Hands-on robotics and coding workshops conducted for students at Calvary Arrows College.\n\nStudents explored foundational electronics, sensor interfacing, and building real-world automated prototypes.",
    status: "Completed",
    programType: "completed",
    eventDate: "2019-11-15",
    imageUrl: cac,
    galleryImages: [twentyTwenty, twentyTw0],
  },
  {
    id: "hist-2",
    title: "Bootcamp 2020",
    description: "An intensive immersion program focusing on Python programming, algorithmic thinking, and practical hardware design.\n\nParticipants worked in teams to build functional STEM demonstration kits.",
    status: "Completed",
    programType: "completed",
    eventDate: "2020-08-20",
    imageUrl: twentyTwenty,
    galleryImages: [cac, twentyTw0],
  },
  {
    id: "hist-3",
    title: "Bootcamp 2022",
    description: "Comprehensive youth empowerment bootcamp covering web development, embedded systems, and collaborative technology innovation.\n\nFeaturing project presentations, interactive mentoring, and award recognitions.",
    status: "Completed",
    programType: "completed",
    eventDate: "2022-09-10",
    imageUrl: twentyTw0,
    galleryImages: [cac, twentyTwenty],
  },
];

const formatEventDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
    }
  } catch (e) {
    // ignore parsing errors
  }
  return dateStr;
};

const Trainings = () => {
  const [programs, setPrograms] = useState(fallbackPrograms);
  const [programsState, setProgramsState] = useState({ loading: true, error: "" });

  // Lightbox state for viewing gallery images in full size
  const [lightbox, setLightbox] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    title: "",
  });

  const openLightbox = (images, index = 0, title = "") => {
    setLightbox({
      isOpen: true,
      images,
      currentIndex: index,
      title,
    });
  };

  const closeLightbox = useCallback(() => {
    setLightbox((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const nextLightboxImage = useCallback(() => {
    setLightbox((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length,
    }));
  }, []);

  const prevLightboxImage = useCallback(() => {
    setLightbox((prev) => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length,
    }));
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightbox.isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLightboxImage();
      if (e.key === "ArrowLeft") prevLightboxImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightbox.isOpen, closeLightbox, nextLightboxImage, prevLightboxImage]);

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const items = await getPrograms();
        if (Array.isArray(items) && items.length > 0) {
          setPrograms(items);
        }
        setProgramsState({ loading: false, error: "" });
      } catch (error) {
        console.error("Unable to load programs:", error);
        setProgramsState({ loading: false, error: "" });
      }
    };

    loadPrograms();
  }, []);

  // Separate programs into upcoming/current vs completed/historical events
  const upcomingPrograms = programs.filter(
    (p) => p.programType !== "completed" && p.status !== "Completed"
  );

  let completedTrainings = programs.filter(
    (p) => p.programType === "completed" || p.status === "Completed"
  );

  // If no completed training records exist in Firestore yet, provide the historical defaults
  if (completedTrainings.length === 0) {
    completedTrainings = fallbackPrograms.filter(
      (p) => p.programType === "completed" || p.status === "Completed"
    );
  }

  return (
    <>
      <div
        style={{
          fontFamily: "'Clash Display', sans-serif",
        }}
      >
        <div
          style={{
            backgroundImage: `linear-gradient(45deg, white, rgba(245, 245, 251, 1), rgba(224, 225, 244), rgba(230, 230, 247, 1), rgba(255, 217, 249), rgba(254, 236, 252), white)`,
          }}
        >
          <TrainingHeader />
          <Announcement />
        </div>

        {/* =========================================================================
            SECTION 1: UPCOMING / CURRENT PROGRAMS
            ========================================================================= */}
        <h1
          style={{
            fontWeight: 550,
          }}
          className="text-center m-5 text-[2rem] md:text-[2rem] text-dark"
        >
          Our Programs
        </h1>
        <div className="w-[90%] md:w-[80%] m-auto pb-[20px]">
          {programsState.loading ? (
            <p className="text-center text-[#4B4B4B]">Loading programs…</p>
          ) : programsState.error ? (
            <p className="text-center text-red-600">{programsState.error}</p>
          ) : upcomingPrograms.length === 0 ? (
            <p className="text-center text-[#4B4B4B]">Upcoming programs will appear here once published from the admin dashboard.</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              {upcomingPrograms.map((program) => {
                const imageUrl = program.imageUrl || fallbackProgramImages[program.title] || null;
                return (
                  <div key={program.id || program.title} className="rounded-lg bg-white p-6 text-left shadow-xl flex flex-col justify-between">
                    <div>
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={program.title}
                          className="mb-4 h-44 w-full rounded-md object-cover bg-slate-50"
                        />
                      )}
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <h3 className="font-bold text-dark text-lg">{program.title}</h3>
                        {program.status && (
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                            program.status === "Open"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-200 text-slate-700"
                          }`}>
                            {program.status}
                          </span>
                        )}
                      </div>
                      {/* Preserving paragraph & line-breaks in description */}
                      <p
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                        className="text-[#4B4B4B] text-sm leading-relaxed break-words whitespace-pre-line"
                      >
                        {program.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* =========================================================================
            SECTION 2: A RECORD OF OUR TRAINING & EVENT GALLERIES
            ========================================================================= */}
        <h1
          style={{
            fontWeight: 550,
          }}
          className="text-center m-5 text-[2rem] md:text-[2rem] text-dark"
        >
          A Record Of Our Training
        </h1>
        <div className="w-[90%] md:w-[80%] m-auto mb-10">
          <p
            style={{
              fontFamily: "'Montserrat', sans-serif",
            }}
            className="text-center text-[#4B4B4B] text-base md:text-lg break-words max-w-3xl mx-auto"
          >
            ExploreStemi from the beginning up till now have carried out several
            bootcamps and other training programs with the aim of equipping
            youths with necessary tech skills.
          </p>
        </div>

        <div className="w-[90%] md:w-[80%] m-auto space-y-12 pb-16">
          {completedTrainings.map((event) => {
            const coverImage = event.imageUrl || fallbackProgramImages[event.title] || null;
            const extraGallery = Array.isArray(event.galleryImages) ? event.galleryImages : [];
            // Combined unique list of images for this training event
            const allImages = [coverImage, ...extraGallery].filter(Boolean);
            const formattedDate = formatEventDate(event.eventDate);

            return (
              <div
                key={event.id || event.title}
                className="rounded-2xl bg-white p-6 md:p-8 text-left shadow-xl border border-slate-100"
              >
                {/* Event Header */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-dark">{event.title}</h2>
                    {formattedDate && (
                      <p className="mt-1 text-xs font-semibold text-primary">
                        📅 {formattedDate}
                      </p>
                    )}
                  </div>
                  <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-purple-700">
                    {event.status || "Completed"}
                  </span>
                </div>

                {/* Event Description with preserved formatting */}
                <p
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-[#4B4B4B] text-sm md:text-base leading-relaxed break-words whitespace-pre-line mb-6"
                >
                  {event.description}
                </p>

                {/* Multi-Photo Event Gallery */}
                {allImages.length > 0 && (
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        Event Pictures ({allImages.length})
                      </h4>
                      <span className="text-[11px] text-slate-400">Click picture to view full size</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {allImages.map((imgSrc, imgIdx) => (
                        <div
                          key={`${event.id || event.title}-img-${imgIdx}`}
                          onClick={() => openLightbox(allImages, imgIdx, event.title)}
                          className="group relative cursor-pointer overflow-hidden rounded-xl bg-slate-100 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ring-1 ring-slate-200/60"
                        >
                          <img
                            src={imgSrc}
                            alt={`${event.title} moment ${imgIdx + 1}`}
                            className="h-48 w-full object-cover transition duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                          {/* Hover overlay indicator */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-slate-800 shadow">
                              🔍 View
                            </span>
                          </div>
                          {imgIdx === 0 && (
                            <span className="absolute bottom-2 left-2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-xs">
                              Cover
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* =========================================================================
          LIGHTBOX MODAL FOR FULL-SIZE IMAGE PREVIEWS
          ========================================================================= */}
      {lightbox.isOpen && lightbox.images.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image gallery lightbox"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm transition-opacity"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Close image viewer"
            className="absolute top-4 right-4 z-50 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur hover:bg-white/40 transition"
          >
            ✕
          </button>

          {/* Navigation - Prev */}
          {lightbox.images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prevLightboxImage();
              }}
              aria-label="Previous image"
              className="absolute left-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-xl text-white backdrop-blur hover:bg-white/40 transition"
            >
              ❮
            </button>
          )}

          {/* Modal Content */}
          <div
            className="relative max-h-[90vh] max-w-4xl overflow-hidden rounded-xl bg-transparent text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.images[lightbox.currentIndex]}
              alt={`${lightbox.title} full view`}
              className="max-h-[80vh] w-auto max-w-full rounded-lg object-contain mx-auto shadow-2xl"
            />
            <div className="mt-3 flex items-center justify-between text-white text-xs px-2">
              <span className="font-semibold text-sm">{lightbox.title}</span>
              <span>
                {lightbox.currentIndex + 1} of {lightbox.images.length}
              </span>
            </div>
          </div>

          {/* Navigation - Next */}
          {lightbox.images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                nextLightboxImage();
              }}
              aria-label="Next image"
              className="absolute right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-xl text-white backdrop-blur hover:bg-white/40 transition"
            >
              ❯
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default Trainings;
